import React, { useMemo, useState } from 'react';
import '../css/ChatCalendar.css';

const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

const pad2 = (n) => String(n).padStart(2, '0');

export const IVR_CALENDAR_SELECTABLE_DAYS = 15;

const toIsoDate = (year, monthIndex, day) => (
  `${year}-${pad2(monthIndex + 1)}-${pad2(day)}`
);

const parseIsoToLocalDate = (iso) => {
  const [year, month, day] = iso.split('-').map(Number);
  return new Date(year, month - 1, day);
};

const getSelectableBounds = () => {
  const now = new Date();
  const maxDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const minDate = new Date(maxDate);
  minDate.setDate(minDate.getDate() - IVR_CALENDAR_SELECTABLE_DAYS);
  return { minDate, maxDate };
};

export const isIvrCalendarDateSelectable = (iso) => {
  if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) {
    return false;
  }
  const date = parseIsoToLocalDate(iso);
  const { minDate, maxDate } = getSelectableBounds();
  return date >= minDate && date <= maxDate;
};

const ChatCalendarPicker = ({ onSelect, disabled = false }) => {
  const today = new Date();
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const [selectedIso, setSelectedIso] = useState(null);

  const monthLabel = useMemo(() => {
    const label = new Date(viewYear, viewMonth, 1).toLocaleString('en-US', {
      month: 'long',
      year: 'numeric',
    });
    return label;
  }, [viewYear, viewMonth]);

  const cells = useMemo(() => {
    const firstOfMonth = new Date(viewYear, viewMonth, 1);
    const startOffset = firstOfMonth.getDay();
    const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
    const daysInPrevMonth = new Date(viewYear, viewMonth, 0).getDate();
    const grid = [];

    for (let i = 0; i < startOffset; i += 1) {
      const day = daysInPrevMonth - startOffset + i + 1;
      const prevMonth = viewMonth === 0 ? 11 : viewMonth - 1;
      const prevYear = viewMonth === 0 ? viewYear - 1 : viewYear;
      grid.push({
        day,
        iso: toIsoDate(prevYear, prevMonth, day),
        outside: true,
      });
    }

    for (let day = 1; day <= daysInMonth; day += 1) {
      grid.push({
        day,
        iso: toIsoDate(viewYear, viewMonth, day),
        outside: false,
      });
    }

    while (grid.length % 7 !== 0) {
      const nextIndex = grid.length - startOffset - daysInMonth + 1;
      const nextMonth = viewMonth === 11 ? 0 : viewMonth + 1;
      const nextYear = viewMonth === 11 ? viewYear + 1 : viewYear;
      grid.push({
        day: nextIndex,
        iso: toIsoDate(nextYear, nextMonth, nextIndex),
        outside: true,
      });
    }

    return grid;
  }, [viewYear, viewMonth]);

  const shiftMonth = (delta) => {
    const next = new Date(viewYear, viewMonth + delta, 1);
    setViewYear(next.getFullYear());
    setViewMonth(next.getMonth());
  };

  const canGoNext = () => (
    viewYear < today.getFullYear()
    || (viewYear === today.getFullYear() && viewMonth < today.getMonth())
  );

  const canGoPrev = () => {
    const { minDate } = getSelectableBounds();
    const firstOfViewMonth = new Date(viewYear, viewMonth, 1);
    const minMonthStart = new Date(minDate.getFullYear(), minDate.getMonth(), 1);
    return firstOfViewMonth > minMonthStart;
  };

  const handleDayClick = (cell) => {
    if (disabled || selectedIso || !isIvrCalendarDateSelectable(cell.iso)) {
      return;
    }
    setSelectedIso(cell.iso);
    if (onSelect) {
      onSelect(cell.iso);
    }
  };

  return (
    <div className={`xbee-calendar-card${disabled ? ' xbee-calendar-card--disabled' : ''}`}>
      <div className="xbee-calendar-header">
        <button
          type="button"
          className="xbee-calendar-nav"
          onClick={() => shiftMonth(-1)}
          disabled={disabled || !canGoPrev()}
          aria-label="Previous month"
        >
          ‹
        </button>
        <span className="xbee-calendar-month">{monthLabel}</span>
        <button
          type="button"
          className="xbee-calendar-nav"
          onClick={() => shiftMonth(1)}
          disabled={disabled || !canGoNext()}
          aria-label="Next month"
        >
          ›
        </button>
      </div>
      <div className="xbee-calendar-weekdays">
        {WEEKDAYS.map((label) => (
          <span key={label} className="xbee-calendar-weekday">{label}</span>
        ))}
      </div>
      <div className="xbee-calendar-grid">
        {cells.map((cell) => {
          const isSelected = selectedIso === cell.iso;
          const isSelectable = isIvrCalendarDateSelectable(cell.iso);
          return (
            <button
              key={cell.iso}
              type="button"
              className={[
                'xbee-calendar-day',
                cell.outside ? 'outside' : '',
                isSelected ? 'selected' : '',
                !isSelectable ? 'unavailable' : '',
              ].filter(Boolean).join(' ')}
              onClick={() => handleDayClick(cell)}
              disabled={disabled || !isSelectable}
            >
              {cell.day}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default ChatCalendarPicker;
